import { useEffect } from "react";
import Hls from "hls.js";
import { create, isPlayerSupported, PlayerEventType } from "amazon-ivs-player";

// The IVS player runs in a WebAssembly worker that must be served as separate files
const wasmWorker = new URL("amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.js", import.meta.url).toString();
const wasmBinary = new URL("amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.wasm", import.meta.url).toString();

/**
 * Plays an IVS live channel in the given <video>. The IVS player keeps viewers ~2-5 s behind the host
 * (low-latency HLS); browsers it doesn't support fall back to hls.js.
 */
const useIvsPlayer = (videoRef, playbackUrl, enabled = true) => {
  useEffect(() => {
    const video = videoRef.current;
    if (!enabled || !playbackUrl || !video) return;

    if (isPlayerSupported) {
      const player = create({ wasmWorker, wasmBinary });
      player.attachHTMLVideoElement(video);
      player.addEventListener(PlayerEventType.ERROR, (error) => console.error("IVS player error:", error));
      player.setAutoplay(true);
      player.load(playbackUrl);
      return () => player.delete();
    }

    if (Hls.isSupported()) {
      const hls = new Hls({ lowLatencyMode: true });
      hls.loadSource(playbackUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
      return () => hls.destroy();
    }

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = playbackUrl;
      video.play().catch(() => {});
    }
  }, [videoRef, playbackUrl, enabled]);
};

export default useIvsPlayer;
